/**
 * A small CommonMark-subset renderer for user-facing prose.
 *
 * WHY IT LIVES IN `components/common`
 * ----------------------------------
 * It started life inside `features/assistant` as the renderer for assistant
 * replies. The Message Center then needed the same guarantees for admin-written
 * notice bodies, and `features/assistant`, `features/support` and
 * `features/tickets` already import each other — adding a fourth node to that
 * cycle blanked the whole app at runtime once, with `tsc` and the production
 * build both green. This module imports nothing from any feature, so hoisting
 * it here costs nothing and removes the temptation.
 *
 * WHY NOT A LIBRARY
 * -----------------
 * The obvious answer is `react-markdown` + `remark-gfm`, but that is ~60 kB of
 * dependency for a feature that needs paragraphs, lists, code, emphasis and
 * links — and adding it means editing `package.json`/`pnpm-lock.yaml`, which
 * belongs to no single agent in a parallel build. This renderer covers the
 * subset an IT answer actually uses, in ~200 lines, with two properties a
 * general-purpose renderer has to be configured into:
 *
 *   1. It never touches `dangerouslySetInnerHTML`. Output is React elements
 *      only, so a model that emits `<img onerror=…>` renders that as visible
 *      text rather than as markup. There is no HTML pass-through to disable.
 *   2. Link hrefs are allow-listed to http/https/mailto, so `javascript:` and
 *      `data:` URLs degrade to plain text.
 *
 * It is also written for *streaming*: an unterminated code fence or emphasis
 * run renders sensibly mid-answer instead of flickering.
 *
 * NOT SUPPORTED (deliberate, and listed so nobody assumes otherwise): tables,
 * nested lists, footnotes, images, raw HTML. If assistant answers start needing
 * tables, swap this module for `react-markdown` behind the same `<Markdown>`
 * prop signature.
 */

import { cn, Separator, Typography } from "@norican/react";
import { Fragment, type ReactNode, useMemo } from "react";

// ─── Inline parsing ──────────────────────────────────────────────────────

type InlineKind = "code" | "link" | "strong" | "em" | "del" | "autolink";

interface InlinePattern {
  kind: InlineKind;
  pattern: RegExp;
}

/**
 * Ordered by precedence, not by appearance: whichever pattern matches earliest
 * in the string wins, and ties are broken by this order. Code spans come first
 * so `` `**not bold**` `` stays literal.
 */
const INLINE_PATTERNS: readonly InlinePattern[] = [
  { kind: "code", pattern: /`([^`\n]+)`/ },
  { kind: "link", pattern: /\[([^\]\n]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/ },
  { kind: "strong", pattern: /\*\*([^\n]+?)\*\*/ },
  { kind: "strong", pattern: /__([^\n]+?)__/ },
  { kind: "del", pattern: /~~([^\n]+?)~~/ },
  { kind: "em", pattern: /\*([^*\n]+?)\*/ },
  { kind: "em", pattern: /(?<![\w])_([^_\n]+?)_(?![\w])/ },
  { kind: "autolink", pattern: /https?:\/\/[^\s<>()[\]]+/ },
];

/** Schemes a link may use. Everything else renders as inert text. */
const SAFE_SCHEMES = ["http:", "https:", "mailto:"];

function safeHref(raw: string): string | null {
  try {
    // `window.location.href` as the base resolves relative links without
    // letting a scheme-relative or absolute hostile URL through.
    const url = new URL(raw, window.location.href);
    return SAFE_SCHEMES.includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

/** Guards against a pathological nesting depth in model output. */
const MAX_INLINE_DEPTH = 5;

function renderInline(source: string, keyPrefix: string, depth = 0): ReactNode {
  if (source === "" || depth >= MAX_INLINE_DEPTH) {
    return source;
  }

  let best: { kind: InlineKind; match: RegExpExecArray } | null = null;
  for (const { kind, pattern } of INLINE_PATTERNS) {
    const match = pattern.exec(source);
    if (match !== null && (best === null || match.index < best.match.index)) {
      best = { kind, match };
    }
  }

  if (best === null) {
    return source;
  }

  const { kind, match } = best;
  const before = source.slice(0, match.index);
  const after = source.slice(match.index + match[0].length);
  const inner = match[1] ?? "";
  const key = `${keyPrefix}-${match.index}`;

  let element: ReactNode;
  switch (kind) {
    case "code":
      element = (
        <code
          key={key}
          className="bg-bg-muted text-fg rounded-sm px-1 py-0.5 font-mono text-[0.9em]"
        >
          {inner}
        </code>
      );
      break;
    case "link":
    case "autolink": {
      const raw = kind === "link" ? (match[2] ?? "") : match[0];
      const label = kind === "link" ? inner : match[0];
      const href = safeHref(raw);
      element =
        href === null ? (
          <Fragment key={key}>{label}</Fragment>
        ) : (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-fg-subtle hover:text-accent-hover underline underline-offset-2"
          >
            {renderInline(label, `${key}-l`, depth + 1)}
          </a>
        );
      break;
    }
    case "strong":
      element = (
        <strong key={key} className="font-weight-semibold">
          {renderInline(inner, `${key}-s`, depth + 1)}
        </strong>
      );
      break;
    case "em":
      element = <em key={key}>{renderInline(inner, `${key}-e`, depth + 1)}</em>;
      break;
    case "del":
      element = (
        <del key={key} className="text-fg-muted">
          {renderInline(inner, `${key}-d`, depth + 1)}
        </del>
      );
      break;
  }

  return (
    <>
      {before}
      {element}
      {renderInline(after, `${keyPrefix}-a${match.index}`, depth)}
    </>
  );
}

// ─── Block parsing ───────────────────────────────────────────────────────

type Block =
  | { kind: "paragraph"; text: string }
  | { kind: "heading"; level: number; text: string }
  | { kind: "code"; language: string; text: string }
  | { kind: "list"; ordered: boolean; items: string[] }
  | { kind: "quote"; blocks: Block[] }
  | { kind: "rule" };

const FENCE = /^\s{0,3}```+\s*([\w+-]*)\s*$/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*)$/;
const RULE = /^\s{0,3}([-*_])\s*(?:\1\s*){2,}$/;
const QUOTE = /^\s{0,3}>\s?(.*)$/;
const UNORDERED = /^\s{0,3}[-*+]\s+(.*)$/;
const ORDERED = /^\s{0,3}\d{1,9}[.)]\s+(.*)$/;

function isBlockStart(line: string): boolean {
  return (
    line.trim() === "" ||
    FENCE.test(line) ||
    HEADING.test(line) ||
    RULE.test(line) ||
    QUOTE.test(line) ||
    UNORDERED.test(line) ||
    ORDERED.test(line)
  );
}

function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index] ?? "";

    if (line.trim() === "") {
      index += 1;
      continue;
    }

    const fence = FENCE.exec(line);
    if (fence !== null) {
      const body: string[] = [];
      index += 1;
      // An unterminated fence is normal mid-stream: treat end-of-input as the
      // closing fence so a code block appears as it is typed.
      while (index < lines.length && !FENCE.test(lines[index] ?? "")) {
        body.push(lines[index] ?? "");
        index += 1;
      }
      index += 1;
      blocks.push({ kind: "code", language: fence[1] ?? "", text: body.join("\n") });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading !== null) {
      blocks.push({
        kind: "heading",
        level: (heading[1] ?? "#").length,
        text: heading[2] ?? "",
      });
      index += 1;
      continue;
    }

    if (RULE.test(line)) {
      blocks.push({ kind: "rule" });
      index += 1;
      continue;
    }

    if (QUOTE.test(line)) {
      const quoted: string[] = [];
      while (index < lines.length) {
        const match = QUOTE.exec(lines[index] ?? "");
        if (match === null) {
          break;
        }
        quoted.push(match[1] ?? "");
        index += 1;
      }
      blocks.push({ kind: "quote", blocks: parseBlocks(quoted.join("\n")) });
      continue;
    }

    const ordered = ORDERED.test(line);
    const listMatch = ordered ? ORDERED.exec(line) : UNORDERED.exec(line);
    if (listMatch !== null) {
      const items: string[] = [];
      while (index < lines.length) {
        const current = lines[index] ?? "";
        const item = ordered ? ORDERED.exec(current) : UNORDERED.exec(current);
        if (item !== null) {
          items.push(item[1] ?? "");
          index += 1;
          continue;
        }
        // An indented, non-blank line continues the previous item rather than
        // starting a paragraph — this is what keeps wrapped bullets together.
        if (current.trim() !== "" && /^\s{2,}\S/.test(current) && items.length > 0) {
          items[items.length - 1] = `${items[items.length - 1]} ${current.trim()}`;
          index += 1;
          continue;
        }
        break;
      }
      blocks.push({ kind: "list", ordered, items });
      continue;
    }

    const paragraph: string[] = [];
    while (index < lines.length && !isBlockStart(lines[index] ?? "")) {
      paragraph.push((lines[index] ?? "").trim());
      index += 1;
    }
    blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
  }

  return blocks;
}

// ─── Rendering ───────────────────────────────────────────────────────────

/**
 * Markdown headings sit *inside* a message — an assistant reply, a Message
 * Center notice — which is already under the page `<h1>` and a section `<h2>`,
 * so level 1 starts at `<h3>`. Getting this wrong is the most common way a chat
 * transcript breaks screen-reader heading navigation.
 */
const HEADING_ELEMENTS = ["h3", "h4", "h5", "h6", "h6", "h6"] as const;
const HEADING_VARIANTS = [
  "heading-4",
  "heading-5",
  "heading-6",
  "heading-6",
  "heading-6",
  "heading-6",
] as const;

function renderBlock(block: Block, key: string): ReactNode {
  switch (block.kind) {
    case "heading": {
      const level = Math.min(Math.max(block.level, 1), 6) - 1;
      return (
        <Typography
          key={key}
          as={HEADING_ELEMENTS[level] ?? "h6"}
          variant={HEADING_VARIANTS[level] ?? "heading-6"}
          className="mt-2 first:mt-0"
        >
          {renderInline(block.text, key)}
        </Typography>
      );
    }

    case "code":
      return (
        <pre
          key={key}
          className="bg-bg-inset border-border-subtle overflow-x-auto rounded-md border p-3"
          {...(block.language === "" ? {} : { "data-language": block.language })}
        >
          <code className="text-fg font-mono text-sm">{block.text}</code>
        </pre>
      );

    case "list": {
      const className = block.ordered ? "list-decimal space-y-1 pl-5" : "list-disc space-y-1 pl-5";
      const items = block.items.map((item, itemIndex) => (
        <li key={`${key}-${itemIndex}`}>
          <Typography as="span" variant="body">
            {renderInline(item, `${key}-${itemIndex}`)}
          </Typography>
        </li>
      ));
      return block.ordered ? (
        <ol key={key} className={className}>
          {items}
        </ol>
      ) : (
        <ul key={key} className={className}>
          {items}
        </ul>
      );
    }

    case "quote":
      return (
        <blockquote key={key} className="border-border-strong flex flex-col gap-2 border-l-2 pl-3">
          {block.blocks.map((child, childIndex) => renderBlock(child, `${key}-q${childIndex}`))}
        </blockquote>
      );

    case "rule":
      return <Separator key={key} />;

    case "paragraph":
      return (
        <Typography key={key} as="p" variant="body">
          {renderInline(block.text, key)}
        </Typography>
      );
  }
}

export interface MarkdownProps {
  /** The raw markdown. Safe to pass a partial document while streaming. */
  children: string;
  className?: string;
}

/**
 * Renders assistant prose.
 *
 * Memoised on the source string because a streaming answer re-renders on every
 * delta and re-parsing a long reply per token is the one place this surface
 * could plausibly get slow.
 */
export function Markdown({ children, className }: MarkdownProps) {
  const blocks = useMemo(() => parseBlocks(children), [children]);

  if (children.trim() === "") {
    return null;
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {blocks.map((block, index) => renderBlock(block, `b${index}`))}
    </div>
  );
}
